// src/components/navigation/SmartPageNavigation.tsx
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome } from 'react-icons/fa';

type SmartPageNavigationProps = {
  className?: string;
  variant?: 'floating' | 'inline';
  forceShow?: boolean;
};

const HOME_PATHS = ['/', '/home', '/accueil'];

const SmartPageNavigation: React.FC<SmartPageNavigationProps> = ({
  className = '',
  variant = 'floating',
  forceShow = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isHomePage = HOME_PATHS.includes(location.pathname);

  if (isHomePage && !forceShow) {
    return null;
  }

  const homeLabel = t('navigation.backHome', {
    defaultValue: "Retour à l’accueil",
  });

  const previousLabel = t('navigation.backPrevious', {
    defaultValue: 'Retour à la page précédente',
  });

  const shortHomeLabel = t('navigation.homeShort', {
    defaultValue: 'Accueil',
  });

  const shortPreviousLabel = t('common.back', {
    defaultValue: 'Retour',
  });

  const handlePrevious = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/');
  };

  const baseClasses =
    'pointer-events-auto inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-full border border-slate-200/80 bg-white/90 p-0 text-xs font-semibold text-slate-700 shadow-sm backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 lg:h-9 lg:w-auto lg:rounded-xl lg:px-2.5 lg:py-1.5 lg:text-xs';

  const wrapperClasses =
    variant === 'floating'
      ? 'fixed left-3 bottom-[calc(env(safe-area-inset-bottom)+4.25rem)] z-[9994] flex flex-row gap-1.5 lg:left-6 lg:bottom-6'
      : 'my-3 flex flex-wrap items-center gap-1.5';

  return (
    <div className={[wrapperClasses, className].filter(Boolean).join(' ')}>
      <button
        type="button"
        onClick={handlePrevious}
        aria-label={previousLabel}
        className={baseClasses}
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-xs transition-colors duration-200 lg:h-6 lg:w-6">
          <FaArrowLeft />
        </span>
        <span className="hidden lg:inline">{shortPreviousLabel}</span>
        <span className="sr-only lg:hidden">{shortPreviousLabel}</span>
      </button>

      <Link to="/" aria-label={homeLabel} className={baseClasses}>
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-xs transition-colors duration-200 lg:h-6 lg:w-6">
          <FaHome />
        </span>
        <span className="hidden lg:inline">{shortHomeLabel}</span>
        <span className="sr-only lg:hidden">{shortHomeLabel}</span>
      </Link>
    </div>
  );
};

export default SmartPageNavigation;
